/**
 * hooks/useChat.js — Phase 4 Week 2
 *
 * Manages the full chatbot conversation state:
 *  • Generates and holds the sessionId UUID for the lifetime of the drawer
 *  • Sends messages via REST POST /hr/chat/message (returns 202)
 *  • Subscribes to /topic/chat/{sessionId} via existing useStomp hook
 *  • Appends streamed tokens to the last assistant message in real time
 *  • Loads conversation history from the backend on mount
 *  • Exposes clearSession() to reset and start a new conversation
 *
 * Message shape:
 *   {
 *     id:        string,   — local UUID for React key
 *     role:      'user' | 'assistant',
 *     content:   string,
 *     streaming: boolean,  — true while tokens are arriving
 *     sqlUsed:   string|null,  — populated on done:true packet (analytics path)
 *     isError:   boolean,
 *     chartType: string|null,  — "line", "bar", or "pie" (analytics path only)
 *     chartData: string|null,  — JSON array of result rows (analytics path only)
 *   }
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { chatApi } from '../services/hrmsApi';
import { useStomp } from './useStomp';

// ── Helpers ───────────────────────────────────────────────────────────────────

function newId() {
  return Math.random().toString(36).slice(2, 11);
}

function generateSessionId() {
  // RFC-4122 v4 UUID
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function makeUserMsg(content)  { return { id: newId(), role: 'user',      content, streaming: false, sqlUsed: null, isError: false, chartType: null, chartData: null, chartTitle: null, yAxisLabel: null }; }
function makeAssistantMsg()    { return { id: newId(), role: 'assistant', content: '', streaming: true,  sqlUsed: null, isError: false, chartType: null, chartData: null, chartTitle: null, yAxisLabel: null }; }

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useChat({ open }) {
  const [messages,   setMessages]   = useState([]);
  const [sessionId,  setSessionId]  = useState(() => generateSessionId());
  const [isLoading,  setIsLoading]  = useState(false); // waiting for first token
  const [error,      setError]      = useState(null);  // send-level error
  const [historyLoaded, setHistoryLoaded] = useState(false);

  // Track the id of the assistant message currently being streamed
  const streamingIdRef = useRef(null);
  const streamingTimeoutRef = useRef(null);   // safety net to clear stuck streaming state

  // Clears any pending streaming safety timeout
  const clearStreamingTimeout = useCallback(() => {
    if (streamingTimeoutRef.current) {
      clearTimeout(streamingTimeoutRef.current);
      streamingTimeoutRef.current = null;
    }
  }, []);

  // Arms a 30-second safety timeout — if streaming never completes (done packet lost),
  // force-clear all streaming flags so the input is never permanently locked.
  const armStreamingTimeout = useCallback(() => {
    clearStreamingTimeout();
    streamingTimeoutRef.current = setTimeout(() => {
      setMessages((prev) =>
        prev.map((m) => m.streaming ? { ...m, streaming: false } : m)
      );
      streamingIdRef.current = null;
      setIsLoading(false);
    }, 30_000);
  }, [clearStreamingTimeout]);

  // Cleanup safety timeout on unmount
  useEffect(() => () => clearStreamingTimeout(), [clearStreamingTimeout]);

  // ── WebSocket subscription ─────────────────────────────────────────────────

  const topic = `/topic/chat/${sessionId}`;

  const onMessage = useCallback((_destination, body) => {
    if (!body) return;
    const { token = '', done = false, sqlUsed = null, error: errMsg = null,
            chartType = null, chartData = null,
            chartTitle = null, yAxisLabel = null } = body;

    if (errMsg) {
      // Backend sent an error packet — clear streaming state with two-pass fallback
      // (same pattern as done branch to guard against any streamingIdRef mismatch)
      clearStreamingTimeout();
      setIsLoading(false);
      setMessages((prev) => {
        const exactMatch = prev.some((m) => m.id === streamingIdRef.current);
        return prev.map((m) => {
          if (exactMatch) {
            return m.id === streamingIdRef.current
              ? { ...m, content: errMsg, streaming: false, isError: true }
              : m;
          } else {
            // Safety path: clear ALL still-streaming messages with the error
            return m.streaming
              ? { ...m, content: errMsg, streaming: false, isError: true }
              : m;
          }
        });
      });
      streamingIdRef.current = null;
      return;
    }

    if (!done) {
      // Streaming token — append to the current assistant message
      setIsLoading(false);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === streamingIdRef.current
            ? { ...m, content: m.content + token }
            : m
        )
      );
    } else {
      // Final packet — mark streaming complete, attach sqlUsed.
      // Use a two-pass update: try exact id match first, then fall back to clearing
      // ALL streaming messages — guards against any streamingIdRef mismatch scenario
      // that would otherwise permanently lock the input bar.
      clearStreamingTimeout();
      setMessages((prev) => {
        const exactMatch = prev.some((m) => m.id === streamingIdRef.current);
        return prev.map((m) => {
          if (exactMatch) {
            // Normal path: update only the matching message
            return m.id === streamingIdRef.current
              ? { ...m, content: m.content + token, streaming: false, sqlUsed, chartType, chartData, chartTitle, yAxisLabel }
              : m;
          } else {
            // Safety path: no id match — clear streaming on any still-streaming message
            return m.streaming
              ? { ...m, streaming: false, sqlUsed: sqlUsed ?? m.sqlUsed,
                  chartType: chartType ?? m.chartType, chartData: chartData ?? m.chartData,
                  chartTitle: chartTitle ?? m.chartTitle, yAxisLabel: yAxisLabel ?? m.yAxisLabel }
              : m;
          }
        });
      });
      streamingIdRef.current = null;
      setIsLoading(false);
    }
  }, []);

  const { status: stompStatus, subscribe, unsubscribe } = useStomp({
    endpoint:  '/ws',
    topics:    [],          // subscribe dynamically per session
    onMessage,
  });

  // Re-subscribe whenever sessionId changes (new conversation)
  useEffect(() => {
    if (!open) return;
    subscribe(topic);
    return () => unsubscribe(topic);
  // eslint-disable-next-line
  }, [sessionId, open]);

  // ── Load history on drawer open ────────────────────────────────────────────

  useEffect(() => {
    if (!open || historyLoaded) return;
    chatApi.getHistory(sessionId)
      .then((turns) => {
        if (turns?.length) {
          setMessages(turns.map((t) => ({
            id:        newId(),
            role:      t.role,
            content:   t.content,
            streaming: false,
            sqlUsed:   t.sqlQuery || null,
            isError:   false,
            chartType: null,
            chartData: null,
          })));
        }
        setHistoryLoaded(true);
      })
      .catch(() => setHistoryLoaded(true)); // silent — empty history is fine
  }, [open, sessionId, historyLoaded]);

  // ── Derived state — must be declared before sendMessage uses it ───────────
  const isStreaming = messages.some((m) => m.streaming);

  // ── Send message ───────────────────────────────────────────────────────────

  const sendMessage = useCallback(async (text) => {
    const trimmed = text?.trim();
    // Guard against both isLoading AND isStreaming — prevents a second message
    // being sent while the first is still streaming, which would overwrite
    // streamingIdRef and permanently lock the first message's streaming:true state.
    if (!trimmed || isLoading || isStreaming) return;

    setError(null);

    // Optimistically add user message
    setMessages((prev) => [...prev, makeUserMsg(trimmed)]);

    // Prepare streaming assistant placeholder
    const assistantMsg = makeAssistantMsg();
    streamingIdRef.current = assistantMsg.id;
    setMessages((prev) => [...prev, assistantMsg]);
    setIsLoading(true);
    armStreamingTimeout();   // safety net: clear stuck state after 30s

    try {
      await chatApi.sendMessage(sessionId, trimmed);
      // Backend returns 202 — answer comes via WebSocket
    } catch (err) {
      const errText = err.response?.data?.message || 'Failed to send message. Please try again.';
      clearStreamingTimeout();
      setError(errText);
      setIsLoading(false);
      // Replace streaming placeholder with error
      setMessages((prev) =>
        prev.map((m) =>
          m.id === streamingIdRef.current
            ? { ...m, content: errText, streaming: false, isError: true }
            : m
        )
      );
      streamingIdRef.current = null;
    }
  }, [sessionId, isLoading, isStreaming, armStreamingTimeout, clearStreamingTimeout]);

  // ── Cancel streaming ───────────────────────────────────────────────────────
  // Allows the user to manually abort a stuck Thinking state without clearing
  // the full session. Replaces the streaming placeholder with a cancelled notice.

  const cancelStreaming = useCallback(() => {
    clearStreamingTimeout();
    setMessages((prev) =>
      prev.map((m) =>
        m.streaming
          ? { ...m, content: 'Response cancelled.', streaming: false, isError: false }
          : m
      )
    );
    streamingIdRef.current = null;
    setIsLoading(false);
  }, [clearStreamingTimeout]);

  const clearSession = useCallback(async () => {
    clearStreamingTimeout();
    try { await chatApi.deleteSession(sessionId); } catch { /* ignore */ }
    unsubscribe(topic);
    const newSession = generateSessionId();
    setSessionId(newSession);
    setMessages([]);
    setIsLoading(false);
    setError(null);
    setHistoryLoaded(false);
    streamingIdRef.current = null;
  }, [sessionId, topic, unsubscribe, clearStreamingTimeout]);

  return {
    messages,
    sessionId,
    isLoading,
    isStreaming,
    stompStatus,
    error,
    sendMessage,
    cancelStreaming,
    clearSession,
  };
}

export default useChat;
